type MedplumLikeError = {
  status?: number;
  message?: string;
  response?: { status?: number; statusText?: string };
  outcome?: {
    issue?: Array<{ code?: string; details?: { text?: string }; diagnostics?: string }>;
  };
};

function asMedplumLikeError(error: unknown): MedplumLikeError | undefined {
  if (error == null || typeof error !== "object") {
    return undefined;
  }
  return error as MedplumLikeError;
}

/** Reads HTTP status from Medplum client errors, including nested fetch response shapes. */
export function getMedplumErrorStatus(error: unknown): number | undefined {
  const medplumError = asMedplumLikeError(error);
  return medplumError?.status ?? medplumError?.response?.status;
}

/** Composes a lowercase message from Medplum error message, response statusText, and OperationOutcome issues. */
export function getMedplumErrorMessage(error: unknown): string {
  const medplumError = asMedplumLikeError(error);
  if (!medplumError) {
    return "";
  }

  const outcomeMessage = medplumError.outcome?.issue
    ?.map((issue) => issue.details?.text ?? issue.diagnostics)
    .filter(Boolean)
    .join(" ");

  return [medplumError.message, medplumError.response?.statusText, outcomeMessage]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
}

/** Reads the OperationOutcome issue codes from Medplum client errors. */
export function getMedplumErrorIssueCodes(error: unknown): string[] {
  const medplumError = asMedplumLikeError(error);
  return (medplumError?.outcome?.issue ?? []).reduce<string[]>(
    (codes, issue) => (issue.code ? [...codes, issue.code] : codes),
    [],
  );
}

/** True when `error` matches an HTTP status code or a substring of the composed error message. */
export function isMedplumHttpError(error: unknown, status: number, messageToken: string): boolean {
  if (getMedplumErrorStatus(error) === status) {
    return true;
  }
  return getMedplumErrorMessage(error).includes(messageToken);
}
